import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getFileUrl } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/layout/Header';
import { Button, Modal } from '../../components/common/Common';
import { Plus, Search, Edit2, Trash2, Download, Eye, Phone, Mail, MapPin, Calendar, Briefcase, FileText, X, SlidersHorizontal, ChevronDown, RotateCcw } from 'lucide-react';
import '../../components/leads/AdvancedSearch.css';
import './AllCandidates.css';

const AllCandidates = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canAdd = hasPermission('hr.add');
  const canEdit = hasPermission('hr.edit');
  const canDelete = hasPermission('hr.delete');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [toast, setToast] = useState(null);
  const limit = 20;

  // Dynamic field maps for display names
  const [sourceMap, setSourceMap] = useState({});
  const [positionMap, setPositionMap] = useState({});
  const [statusMap, setStatusMap] = useState({});

  const showToast = (msg, type = 'success') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Fetch candidate option lookups (matches AddCandidate's data source)
  useEffect(() => {
    const ID_KEYS = {
      candidates_source: 'cs_id',
      candidates_post: 'cp_id',
      candidates_status: 'cst_id',
    };
    const fetchFieldMap = async (type, setter) => {
      try {
        const res = await apiFetch(`candidate-options/${type}`);
        if (res.success && res.data) {
          const items = Array.isArray(res.data) ? res.data : res.data.items || [];
          const idKey = ID_KEYS[type];
          const map = {};
          items.forEach((item) => {
            const id = item[idKey] ?? item.id ?? item.df_id;
            if (id != null) map[id] = item.name || item.label || item.value || '';
          });
          setter(map);
        }
      } catch (err) {
        console.error(`Failed to fetch ${type}:`, err);
      }
    };

    fetchFieldMap('candidates_source', setSourceMap);
    fetchFieldMap('candidates_post', setPositionMap);
    fetchFieldMap('candidates_status', setStatusMap);
  }, []);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search.trim()) params.append('search', search.trim());
      if (filterSource) params.append('source_id', filterSource);
      if (filterPosition) params.append('position_id', filterPosition);
      if (filterStatus) params.append('status_id', filterStatus);
      const res = await apiFetch(`candidates?${params.toString()}`);
      if (res.success) {
        setCandidates(res.data || []);
        setSelectedIds([]);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    } catch (err) {
      console.error('Fetch candidates error:', err);
    }
    setLoading(false);
  }, [page, search, filterSource, filterPosition, filterStatus]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // Reset page when search/filters change
  useEffect(() => {
    setPage(1);
  }, [search, filterSource, filterPosition, filterStatus]);

  const activeFilterCount = [filterSource, filterPosition, filterStatus].filter(Boolean).length;
  const resetFilters = () => {
    setFilterSource('');
    setFilterPosition('');
    setFilterStatus('');
  };
  const mapToOptions = (m) =>
    Object.entries(m).map(([id, name]) => ({ id, name }));

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await apiFetch(`candidates/${deleteId}`, { method: 'DELETE' });
      if (res.success) {
        showToast('Candidate deleted');
        fetchCandidates();
      } else {
        showToast(res.message || 'Failed to delete', 'error');
      }
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const allPageSelected =
    candidates.length > 0 && candidates.every((c) => selectedIds.includes(c.cand_id));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !candidates.find((c) => c.cand_id === id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...candidates.map((c) => c.cand_id)])]);
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    let ok = 0;
    for (const id of ids) {
      try {
        const res = await apiFetch(`candidates/${id}`, { method: 'DELETE' });
        if (res.success) ok++;
      } catch (err) {}
    }
    setShowBulkDeleteModal(false);
    setSelectedIds([]);
    showToast(`${ok} candidate${ok === 1 ? '' : 's'} deleted`);
    fetchCandidates();
  };

  const formatDate = (dt) =>
    dt
      ? new Date(dt).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '-';

  const getStatusColor = (statusName) => {
    const name = (statusName || '').toLowerCase();
    if (name.includes('select') || name.includes('hired') || name.includes('accept')) return '#22c55e';
    if (name.includes('reject') || name.includes('fail')) return '#ef4444';
    if (name.includes('hold') || name.includes('pend') || name.includes('wait')) return '#f59e0b';
    if (name.includes('interview') || name.includes('process')) return '#3b82f6';
    return '#6b7280';
  };

  return (
    <div>
      <Header
        title="All Candidates"
        subtitle={`${pagination.total || candidates.length} candidates`}
        actions={canAdd ? (
          <Button variant="gold" icon={Plus} onClick={() => navigate('/hr/candidates/add')}>
            Add Candidate
          </Button>
        ) : null}
      />
      <div className="page">
        {/* Search + Filters (same design as Leads) */}
        <div className="adv-search">
          <div className="adv-search__bar adv-search__bar--no-export">
            <div className="adv-search__input-wrap">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search by name, mobile, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="adv-search__input"
              />
              {search && (
                <button className="adv-search__clear-input" onClick={() => setSearch('')}>
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              type="button"
              className="adv-search__filter-mobile"
              onClick={() => setShowFilters(!showFilters)}
              aria-label="Open filters"
            >
              <SlidersHorizontal size={16} />
            </button>
            <button
              className={`adv-search__toggle ${showFilters ? 'adv-search__toggle--active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal size={16} />
              Filters
              {activeFilterCount > 0 && <span className="adv-search__count">{activeFilterCount}</span>}
              <ChevronDown size={14} className={showFilters ? 'adv-search__chevron--open' : ''} />
            </button>
            {activeFilterCount > 0 && (
              <button className="adv-search__reset" onClick={resetFilters}>
                <RotateCcw size={14} /> Reset
              </button>
            )}
          </div>

          {showFilters && (
            <div className="adv-search__panel animate-fade-in">
              <h4 className="adv-search__panel-title">Filters</h4>
              <div className="adv-search__grid">
                <div className="adv-search__field">
                  <label>Source</label>
                  <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
                    <option value="">All Sources</option>
                    {mapToOptions(sourceMap).map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>
                <div className="adv-search__field">
                  <label>Position</label>
                  <select value={filterPosition} onChange={(e) => setFilterPosition(e.target.value)}>
                    <option value="">All Positions</option>
                    {mapToOptions(positionMap).map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>
                <div className="adv-search__field">
                  <label>Status</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">All Statuses</option>
                    {mapToOptions(statusMap).map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bulk actions */}
        {selectedIds.length > 0 && (
          <div className="cand-bulk-bar">
            <span className="cand-bulk-bar__count">{selectedIds.length} selected</span>
            <div className="cand-bulk-bar__actions">
              <button className="cand-bulk-bar__clear" onClick={() => setSelectedIds([])}>
                Clear
              </button>
              {canDelete && (
                <button
                  className="cand-bulk-bar__delete"
                  onClick={() => setShowBulkDeleteModal(true)}
                >
                  <Trash2 size={14} /> Delete Selected
                </button>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="cand-table-wrap">
          <table className="cand-table">
            <thead>
              <tr>
                <th className="cand-table__check-col">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th>Name</th>
                <th>Contact</th>
                <th>Source</th>
                <th>Position</th>
                <th>Status</th>
                <th>Location</th>
                <th>DOB</th>
                <th>CV</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="cand-table__empty">
                    Loading...
                  </td>
                </tr>
              ) : candidates.length === 0 ? (
                <tr>
                  <td colSpan="10" className="cand-table__empty">
                    No candidates found
                  </td>
                </tr>
              ) : (
                candidates.map((cand) => {
                  const statusName = statusMap[cand.status_id] || '-';
                  const statusColor = getStatusColor(statusName);
                  return (
                    <tr key={cand.cand_id} className={`cand-table__row${selectedIds.includes(cand.cand_id) ? ' cand-table__row--selected' : ''}`}>
                      <td className="cand-table__check-col">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(cand.cand_id)}
                          onChange={() => toggleSelect(cand.cand_id)}
                        />
                      </td>
                      <td className="cand-table__td--name">
                        <div className="cand-table__name-cell">
                          <div className="cand-table__avatar">
                            {cand.name
                              ? cand.name
                                  .split(/[\s._]/)
                                  .map((n) => n[0])
                                  .join('')
                                  .toUpperCase()
                                  .slice(0, 2)
                              : '?'}
                          </div>
                          <span className="cand-table__name">{cand.name}</span>
                        </div>
                      </td>
                      <td data-label="Contact">
                        <div className="cand-table__contact">
                          <span>{cand.country_code} {cand.mobile}</span>
                          {cand.email && <span className="cand-table__email-text">{cand.email}</span>}
                        </div>
                      </td>
                      <td data-label="Source">
                        <span className="cand-table__text">{sourceMap[cand.source_id] || '-'}</span>
                      </td>
                      <td data-label="Position">
                        <span className="cand-table__text">{positionMap[cand.position_id] || '-'}</span>
                      </td>
                      <td data-label="Status">
                        <span
                          className="cand-status-badge"
                          style={{
                            background: statusColor + '15',
                            color: statusColor,
                          }}
                        >
                          {statusName}
                        </span>
                      </td>
                      <td data-label="Location">
                        <span className="cand-table__text">{cand.location || '-'}</span>
                      </td>
                      <td data-label="DOB">
                        <span className="cand-table__date">{formatDate(cand.date_of_birth)}</span>
                      </td>
                      <td data-label="CV">
                        {cand.cv_path ? (
                          <a
                            href={getFileUrl(cand.cv_path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cand-cv-link"
                            title={cand.cv_name || 'Download CV'}
                          >
                            <Download size={14} />
                            <span>CV</span>
                          </a>
                        ) : (
                          <span className="cand-table__text">-</span>
                        )}
                      </td>
                      <td className="cand-table__td--actions">
                        <div className="cand-table__actions">
                          <button
                            className="cand-action-btn"
                            title="View details"
                            onClick={() => setViewItem(cand)}
                          >
                            <Eye size={15} />
                          </button>
                          {canEdit && (
                            <button
                              className="cand-action-btn"
                              title="Edit"
                              onClick={() => navigate(`/hr/candidates/edit/${cand.cand_id}`)}
                            >
                              <Edit2 size={15} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="cand-action-btn cand-action-btn--danger"
                              title="Delete"
                              onClick={() => {
                                setDeleteId(cand.cand_id);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="cand-pagination">
            <span className="cand-pagination__info">
              Page {page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <div className="cand-pagination__btns">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <button disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Modal */}
      <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="Candidate Details" size="md">
        {viewItem && (() => {
          const statusName = statusMap[viewItem.status_id] || '-';
          const statusColor = getStatusColor(statusName);
          const initials = viewItem.name
            ? viewItem.name.split(/[\s._]/).map(n => n[0]).join('').toUpperCase().slice(0, 2)
            : '?';
          return (
            <div className="cand-view">
              <div className="cand-view__hero">
                <div className="cand-view__avatar">{initials}</div>
                <div className="cand-view__hero-main">
                  <div className="cand-view__name">{viewItem.name || '-'}</div>
                  <div className="cand-view__sub">
                    <span style={{ background: statusColor + '15', color: statusColor }} className="cand-status-badge">
                      {statusName}
                    </span>
                    <span className="cand-view__sub-text">{positionMap[viewItem.position_id] || 'Position not set'}</span>
                  </div>
                </div>
              </div>

              <div className="cand-view__grid">
                <div className="cand-view__field">
                  <span className="cand-view__label"><Phone size={12} /> Mobile</span>
                  <span className="cand-view__value">{viewItem.country_code || '+91'} {viewItem.mobile || '-'}</span>
                </div>
                {viewItem.alt_mobile && (
                  <div className="cand-view__field">
                    <span className="cand-view__label"><Phone size={12} /> Alt. Mobile</span>
                    <span className="cand-view__value">{viewItem.alt_country_code || '+91'} {viewItem.alt_mobile}</span>
                  </div>
                )}
                <div className="cand-view__field">
                  <span className="cand-view__label"><Mail size={12} /> Email</span>
                  <span className="cand-view__value">{viewItem.email || '-'}</span>
                </div>
                <div className="cand-view__field">
                  <span className="cand-view__label"><Briefcase size={12} /> Source</span>
                  <span className="cand-view__value">{sourceMap[viewItem.source_id] || '-'}</span>
                </div>
                <div className="cand-view__field">
                  <span className="cand-view__label"><Briefcase size={12} /> Position</span>
                  <span className="cand-view__value">{positionMap[viewItem.position_id] || '-'}</span>
                </div>
                <div className="cand-view__field">
                  <span className="cand-view__label"><MapPin size={12} /> Location</span>
                  <span className="cand-view__value">{viewItem.location || '-'}</span>
                </div>
                <div className="cand-view__field">
                  <span className="cand-view__label"><Calendar size={12} /> Date of Birth</span>
                  <span className="cand-view__value">{formatDate(viewItem.date_of_birth)}</span>
                </div>
                <div className="cand-view__field">
                  <span className="cand-view__label"><Calendar size={12} /> Added On</span>
                  <span className="cand-view__value">{formatDate(viewItem.create_dt || viewItem.created_at)}</span>
                </div>
                <div className="cand-view__field cand-view__field--full">
                  <span className="cand-view__label"><FileText size={12} /> CV</span>
                  <span className="cand-view__value">
                    {viewItem.cv_path ? (
                      <a href={getFileUrl(viewItem.cv_path)} target="_blank" rel="noopener noreferrer" className="cand-cv-link">
                        <Download size={14} />
                        <span>{viewItem.cv_name || 'Download CV'}</span>
                      </a>
                    ) : '-'}
                  </span>
                </div>
                {viewItem.notes && (
                  <div className="cand-view__field cand-view__field--full">
                    <span className="cand-view__label"><FileText size={12} /> Notes</span>
                    <span className="cand-view__value cand-view__notes">{viewItem.notes}</span>
                  </div>
                )}
              </div>

              <div className="modal__actions">
                <Button variant="outline" onClick={() => setViewItem(null)}>Close</Button>
                {canEdit && <Button variant="gold" icon={Edit2} onClick={() => { const id = viewItem.cand_id; setViewItem(null); navigate(`/hr/candidates/edit/${id}`); }}>Edit</Button>}
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Candidate" size="sm">
        <p style={{ color: 'var(--gray-600)', marginBottom: 20 }}>
          Are you sure you want to delete this candidate? This action cannot be undone.
        </p>
        <div className="modal__actions">
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" icon={Trash2} onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>

      {/* Bulk Delete Modal */}
      <Modal isOpen={showBulkDeleteModal} onClose={() => setShowBulkDeleteModal(false)} title="Delete Candidates" size="sm">
        <p style={{ color: 'var(--gray-600)', marginBottom: 20 }}>
          Delete <strong>{selectedIds.length}</strong> selected candidate{selectedIds.length === 1 ? '' : 's'}? This action cannot be undone.
        </p>
        <div className="modal__actions">
          <Button variant="outline" onClick={() => setShowBulkDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" icon={Trash2} onClick={handleBulkDelete}>
            Delete All
          </Button>
        </div>
      </Modal>

      {toast && <div className={`broker-toast broker-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
};

export default AllCandidates;
